/**
 * Derive a world-space perch point on the active character's head from the
 * same sizing inputs that drive the rendered player sprite.
 */

import type { PlayableCharacterDef } from '@/world-prototype/player/playableCharacters';
import type { SpawnPoint } from '@/world-prototype/types';
import {
  characterBaseWidthFrac,
  yardScaleFor,
} from '@/world-prototype/scale/worldScale';

/** Inset from the sprite top (fraction of rendered height) where frog feet sit. */
const CROWN_INSET_FROM_TOP = 0.055;

export interface CharacterHeadAnchorInput {
  feet: SpawnPoint;
  /** Presence depth scale at the character's current Y. */
  depthScale: number;
  character: PlayableCharacterDef;
  yardId: string;
  worldAspect: number;
}

export interface CharacterHeadAnchorDomInput {
  feet: SpawnPoint;
  worldRect: DOMRectReadOnly;
  characterRect: DOMRectReadOnly;
}

/** Prefer live DOM bounds — matches the painted sprite on screen. */
export function characterHeadPerchAnchorFromDom(
  input: CharacterHeadAnchorDomInput,
): SpawnPoint | null {
  const { worldRect, characterRect } = input;
  if (
    worldRect.width <= 0 ||
    worldRect.height <= 0 ||
    characterRect.height <= 4
  ) {
    return null;
  }
  const crownInsetPx = characterRect.height * CROWN_INSET_FROM_TOP;
  return {
    x:
      (characterRect.left + characterRect.width / 2 - worldRect.left) /
      worldRect.width,
    y:
      (characterRect.top + crownInsetPx - worldRect.top) / worldRect.height,
  };
}

/** Fallback when DOM is not ready — uses registry sizing. */
export function characterHeadPerchAnchor(
  input: CharacterHeadAnchorInput,
): SpawnPoint {
  const { feet, depthScale, character, yardId, worldAspect } = input;
  const yard = yardScaleFor(yardId);
  const imageAspect = character.imageHeight / Math.max(1, character.imageWidth);
  const aspect = worldAspect > 0 ? worldAspect : 16 / 9;
  const baseWidth = characterBaseWidthFrac(
    {
      ...yard,
      characterHeightFeet: character.heightFeet,
      characterDisplayScale: character.displayScale,
    },
    aspect,
    imageAspect,
  );
  const widthFrac = baseWidth * Math.max(0.01, depthScale);
  const heightFrac = (widthFrac * imageAspect) / aspect;
  const spriteTopY = feet.y - heightFrac;
  return {
    x: feet.x,
    y: spriteTopY + heightFrac * CROWN_INSET_FROM_TOP,
  };
}
